/* eslint-disable react-refresh/only-export-components -- a context module exports its
   provider and the hook that reads it together; makeFmt is the same formatting logic
   without React, used by App and by the tests. Splitting them would only move the
   warning, and the cost is a full reload instead of a hot update in dev. */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { annualDistance } from "./calc";
import { LOCALE, MINOR_PER_MAJOR } from "./constants";
import { CURRENCY_MINOR, CURRENCY_SYMBOLS } from "./content";
import type { Currency, DistanceUnit, Profile } from "./types";

export interface Fmt {
  unit: DistanceUnit;
  symbol: string;
  minor: string;
  /** Splits a per-distance cost (in pence/cents) for display: "62.3" + "p", or "£" + "12.75" once it reaches 100. */
  parts: (n: number) => { prefix: string; value: string; suffix: string };
  /** "62.3p", or "£12.75" once it reaches 100p */
  per: (n: number) => string;
  /** "62.3p/mi", or "£12.75/mi" once it reaches 100p */
  rate: (n: number) => string;
  /** True when a cost is shown in currency units (100p/c or more). */
  isMajor: (n: number) => boolean;
  /** Like per/rate but forced to one unit, so several figures can be compared like for like. */
  perAs: (n: number, major: boolean) => string;
  rateAs: (n: number, major: boolean) => string;
  /** "£1,234" */
  money: (n: number) => string;
  /** "8,000 mi" */
  dist: (n: number) => string;
  /** "8,000 mi/yr · 80% home" */
  profileMeta: (p: Profile) => string;
}

/** Decimal places used for each of the two ways a per-distance cost is shown. */
const MAJOR_DP = 2;
const MINOR_DP = 1;

export function makeFmt(currency: Currency, unit: DistanceUnit): Fmt {
  const symbol = CURRENCY_SYMBOLS[currency];
  const minor = CURRENCY_MINOR[currency];
  // Costs of 100p/c or more read better as whole currency units with two decimals.
  // Rounding first so a figure that would print as "100.0p" moves up instead.
  const isMajor = (n: number) => Math.round(n * 10 ** MINOR_DP) / 10 ** MINOR_DP >= MINOR_PER_MAJOR;
  const partsAs = (n: number, major: boolean) =>
    major
      ? { prefix: symbol, value: (Math.round(n) / MINOR_PER_MAJOR).toFixed(MAJOR_DP), suffix: "" }
      : { prefix: "", value: n.toFixed(MINOR_DP), suffix: minor };
  const perAs = (n: number, major: boolean) => {
    const x = partsAs(n, major);
    return x.prefix + x.value + x.suffix;
  };
  const dist = (n: number) => `${Math.round(n).toLocaleString(LOCALE)} ${unit}`;
  return {
    unit,
    symbol,
    minor,
    dist,
    isMajor,
    perAs,
    parts: (n) => partsAs(n, isMajor(n)),
    per: (n) => perAs(n, isMajor(n)),
    rate: (n) => `${perAs(n, isMajor(n))}/${unit}`,
    rateAs: (n, major) => `${perAs(n, major)}/${unit}`,
    money: (n) => symbol + n.toLocaleString(LOCALE, { maximumFractionDigits: 0 }),
    profileMeta: (pr) => `${dist(annualDistance(pr))}/yr · ${Math.round(pr.homeShare)}% home`,
  };
}

const FmtContext = createContext<Fmt>(makeFmt("GBP", "mi"));

export function FmtProvider({ currency, unit, children }: { currency: Currency; unit: DistanceUnit; children: ReactNode }) {
  const fmt = useMemo(() => makeFmt(currency, unit), [currency, unit]);
  return <FmtContext.Provider value={fmt}>{children}</FmtContext.Provider>;
}

export function useFmt(): Fmt {
  return useContext(FmtContext);
}
